import { interpolateDate, interpolateNumber } from '@mui/x-charts-vendor/d3-interpolate';
import { type ContinuousColorConfig } from '../../../models/colorMapping';

const PX_PRECISION = 10;

type ChartsContinuousGradientProps = {
  isReversed?: boolean;
  gradientId: string;
  size: number;
  direction: 'x' | 'y';
  scale: (value: any) => number | undefined;
  colorMap: ContinuousColorConfig;
  colorScale: (value: any) => string | null;
  /**
   * Defines the coordinate base to use:
   * - 'userSpaceOnUse': uses the coordinate of the SVG (values in px).
   * - 'objectBoundingBox': uses the coordinate ot the object on which gradient is applied (values from 0 to 1).
   */
  gradientUnits?: 'objectBoundingBox' | 'userSpaceOnUse';
};

export default function ChartsContinuousGradient(props: ChartsContinuousGradientProps) {
  const { gradientUnits, isReversed, gradientId, size, direction, scale, colorScale, colorMap } =
    props;

  const extremumValues = [colorMap.min ?? 0, colorMap.max ?? 100] as
    | [number, number]
    | [Date, Date];
  const extremumPositions = extremumValues.map(scale).filter((p): p is number => p !== undefined);

  if (extremumPositions.length !== 2) {
    return null;
  }

  const interpolator =
    typeof extremumValues[0] === 'number'
      ? interpolateNumber(extremumValues[0], extremumValues[1])
      : interpolateDate(extremumValues[0], extremumValues[1] as Date);
  const numberOfPoints = Math.round(
    (Math.max(...extremumPositions) - Math.min(...extremumPositions)) / PX_PRECISION,
  );

  const keyPrefix = `${extremumValues[0]}-${extremumValues[1]}-`;
  return (
    <linearGradient
      id={gradientId}
      x1="0"
      x2="0"
      y1="0"
      y2="0"
      {...{
        [`${direction}${isReversed ? 1 : 2}`]:
          gradientUnits === 'objectBoundingBox' ? 1 : `${size}px`,
      }}
      gradientUnits={gradientUnits ?? 'userSpaceOnUse'} // Use the SVG coordinate instead of the component ones.
    >
      {Array.from({ length: numberOfPoints + 1 }, (_, index) => {
        const value = interpolator(index / numberOfPoints);
        if (value === undefined) {
          return null;
        }
        const x = scale(value);
        if (x === undefined) {
          return null;
        }
        const offset = isReversed ? 1 - x / size : x / size;
        const color = colorScale(value);

        if (color === null) {
          return null;
        }
        return <stop key={keyPrefix + index} offset={offset} stopColor={color} stopOpacity={1} />;
      })}
    </linearGradient>
  );
}
